import { useEffect, useRef, useState } from "react";

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
let scriptPromise = null;

function loadGsi() {
  if (window.google?.accounts?.id) return Promise.resolve(true);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
    setTimeout(() => resolve(!!window.google?.accounts?.id), 4000);
  });
  return scriptPromise;
}

// Robust, self-contained Google Sign-In. Loads GSI lazily and degrades
// gracefully (never crashes) when the script is blocked or origin is not
// yet authorized in the Google console.
export default function GoogleSignInButton({ onCredential }) {
  const ref = useRef(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    if (!CLIENT_ID) { setFailed(true); return; }
    loadGsi().then((ok) => {
      if (!active) return;
      if (!ok || !window.google?.accounts?.id) { setFailed(true); return; }
      try {
        window.google.accounts.id.initialize({
          client_id: CLIENT_ID,
          callback: (resp) => resp?.credential && onCredential(resp.credential),
        });
        if (ref.current) {
          window.google.accounts.id.renderButton(ref.current, {
            theme: "filled_black", size: "large", shape: "pill", width: 320, text: "continue_with",
          });
        }
        setReady(true);
      } catch {
        setFailed(true);
      }
    });
    return () => { active = false; };
  }, [onCredential]);

  if (failed) {
    return (
      <div className="text-center text-xs text-zinc-600 px-4 py-2.5 rounded-full glass" data-testid="google-fallback">
        Google sign-in available once this domain is authorized
      </div>
    );
  }
  return (
    <div className="min-h-[44px] flex items-center justify-center">
      <div ref={ref} />
      {!ready && <div className="text-xs text-zinc-600">Loading Google…</div>}
    </div>
  );
}
